import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";
import axios from "axios";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(
    localStorage.getItem("userAdminName") || null
  );
  const [pkCredentialMasterId, setPkCredentialMasterId] = useState(
    localStorage.getItem("pkCredentialMasterIdAdmin") || null
  );
  const [token, setToken] = useState(
    localStorage.getItem("authTokenAdmin") || null
  );
  const [emailId, setEmailId] = useState(
    localStorage.getItem("emailId") || null
  );
  const [role, setRole] = useState(
    localStorage.getItem("userRoleAdmin") || null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  // Set by any API call that gets back a 401/403 (expired or invalid token)
  // — drives the global "session expired" modal instead of each page
  // handling it inline with its own toast.
  const [sessionExpired, setSessionExpired] = useState(false);
  // isOTPVerified: only used by the forgot-password flow, currently disabled (no entry
  // point on the Login page). Uncomment along with sendOTP/verifyOTP/forgotPassword/
  // updatePassword below to re-enable.
  // const [isOTPVerified, setIsOTPVerified] = useState(false);

  // useEffect(() => {
  //   if (!token) {
  //     setUser(null);
  //   }
  // }, [token]);

  useEffect(() => {
    if (!token) return;

    const loginTimestamp = localStorage.getItem("loginTimestampAdmin");
    if (!loginTimestamp) return;

    const expiryTime = 5 * 60 * 60 * 1000; // 5 hour
    const timeLeft = expiryTime - (new Date().getTime() - loginTimestamp);

    if (timeLeft <= 0) {
      logout();
      return;
    }

    const timeout = setTimeout(() => {
      logout();
      navigate("/log-in");
      window.location.reload(); // Force refresh to prevent navigating back to the dashboard
    }, timeLeft);

    return () => clearTimeout(timeout);
  }, [token]);

  // Utility Functions
  const encodeData = (data) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(data));
  // decodeData: only used by the disabled verifyOTP below. Uncomment together.
  // const decodeData = (encodedData) =>
  //   CryptoJS.enc.Utf8.stringify(CryptoJS.enc.Base64.parse(encodedData));

  // OTP Functions — disabled along with the forgot-password flow (see isOTPVerified above).
  // const sendOTP = async (email) => {
  //   try {
  //     setLoading(true);
  //     const randomOTP = Math.floor(1000 + Math.random() * 9000);
  //     const encodedOTP = encodeData(randomOTP.toString());
  //     localStorage.setItem("emailAdminOTP", encodedOTP);
  //     await axios.post(
  //       `${process.env.REACT_APP_API_BASE_URL}/register/emailVerification`,
  //       { email, otp: encodedOTP }
  //     );
  //     // setSuccessMessage("OTP sent successfully!");
  //     toast.success("OTP sent successfully!");
  //   } catch (err) {
  //     // setError(err.response?.data || "Failed to send OTP");
  //     toast.error("Failed to send OTP");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const verifyOTP = (otp) => {
  //   const storedEncodedOTP = localStorage.getItem("emailAdminOTP");
  //   if (!storedEncodedOTP) {
  //     setError("No OTP found. Please request a new OTP.");
  //     return;
  //   }
  //   if (otp.toString() === decodeData(storedEncodedOTP)) {
  //     setIsOTPVerified(true);
  //     // setSuccessMessage("OTP verified successfully!");
  //     toast.success("OTP verified successfully!");

  //     return "OTP verified successfully!";
  //   } else {
  //     // setError("Invalid OTP");
  //     toast.error("Invalid OTP");
  //   }
  // };

  // Authentication Functions
  const registerUser = async ({ email, name, password, number }) => {
    try {
      setLoading(true);
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/register/add`, {
        emailId: email,
        password: encodeData(password),
        name,
        contactNo: number,
      });
      // setSuccessMessage("User registered successfully!");
      toast.success("User registered successfully!");
    } catch (err) {
      // setError(err.response?.data || "Registration failed");
      toast.error("Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // const loginUser = async ({ emailAddress, password }) => {
  //   try {
  //     setLoading(true);
  //     const response = await axios.post(
  //       `${process.env.REACT_APP_API_BASE_URL}/token`,
  //       {
  //         emailAddress,
  //         password: encodeData(password),
  //       }
  //     );
  //     const userData = response?.data?.data[0];
  //     setUser(userData.name);
  //     setToken(userData.token);
  //     setEmailId(userData.emailId);
  //     setPkCredentialMasterId(userData.pkCredentialMasterId);
  //     localStorage.setItem("userAdminName", userData.name);
  //     localStorage.setItem(
  //       "pkCredentialMasterIdAdmin",
  //       userData.pkCredentialMasterId
  //     );
  //     localStorage.setItem("authTokenAdmin", userData.token);
  //     localStorage.setItem("emailId", userData.emailId);
  //     localStorage.setItem("loginTimestampAdmin", new Date().getTime());
  //     // toast.success("Login successful!");
  //   } catch (err) {
  //     setError(err.response?.data?.message || "Login failed");
  //     // toast.error("Login failed");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Auth.jsx (Modify the loginUser function)
const loginUser = async ({ emailAddress, password }) => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/token`,
      {
        emailAddress,
        password: encodeData(password),
      }
    );

    // If the backend returns "Failed" but with a 200 status, 
    // we should still return the data so the UI can handle it.
    if (response?.data?.status === "Successful") {
      const userData = response?.data?.data[0];
      setUser(userData.name);
      setToken(userData.token);
      setEmailId(userData.emailId);
      setPkCredentialMasterId(userData.pkCredentialMasterId);
      setRole(userData.role);

      localStorage.setItem("userAdminName", userData.name);
      localStorage.setItem("pkCredentialMasterIdAdmin", userData.pkCredentialMasterId);
      localStorage.setItem("authTokenAdmin", userData.token);
      localStorage.setItem("emailId", userData.emailId);
      localStorage.setItem("userRoleAdmin", userData.role);
      localStorage.setItem("loginTimestampAdmin", new Date().getTime());
    }

    // ✅ CRITICAL FIX: You must return the response data
    return response.data; 

  } catch (err) {
    // If it's a real 401/404/500 error, return the error object
    const errorData = err.response?.data || { status: "Failed", message: "Login failed" };
    setError(errorData.message);
    return errorData; // ✅ Return this so Login.jsx catch block or logic gets it
  } finally {
    setLoading(false);
  }
};

  // Token Expiry Check
  const isTokenExpired = () => {
    const loginTimestamp = localStorage.getItem("loginTimestampAdmin");
    if (loginTimestamp) {
      const expiryTime = 5 * 60 * 60 * 1000; // 5 hour expiry
      return new Date().getTime() - loginTimestamp > expiryTime;
    }
    return true;
  };

  // Logout Function
  const logout = () => {
    setUser(null);
    setToken(null);
    setPkCredentialMasterId(null);
    setRole(null);
    localStorage.removeItem("userAdminName");
    localStorage.removeItem("authTokenAdmin");
    localStorage.removeItem("pkCredentialMasterIdAdmin");
    localStorage.removeItem("emailId");
    localStorage.removeItem("userRoleAdmin");
    localStorage.removeItem("loginTimestampAdmin");
    toast.info("Logged out successfully", {
      autoClose: 1000,
    });
    navigate("/log-in");
  };

  // Any page's API call can report a 401/403 here instead of handling it
  // inline — shows the global "session expired" modal (see App.js).
  const triggerSessionExpired = () => {
    setSessionExpired(true);
  };

  // Called when the user dismisses the session-expired modal — logs them
  // out for real and sends them back to the login screen.
  const confirmSessionExpired = () => {
    setSessionExpired(false);
    logout();
  };

  // Forgot Password Function
  // const forgotPassword = async (email) => {
  //   try {
  //     setLoading(true);
  //     await axios.post(`${process.env.REACT_APP_API_BASE_URL}/getUserDetail`, { emailId: email });
  //     await sendOTP(email);
  //     toast.success("Recovery email sent successfully!");
  //   } catch (err) {
  //     toast.error(err.response?.data || "Failed to send recovery email");
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  // forgotPassword: disabled along with the rest of the forgot-password flow above.
  // const forgotPassword = async (email) => {
  //   try {
  //     setLoading(true);

  //     // Step 1: Check if user exists
  //     const response = await axios.post(
  //       `${process.env.REACT_APP_API_BASE_URL}/getUserDetail`,
  //       { emailId: email }
  //     );

  //     if (
  //       response.data?.status !== "Successful" ||
  //       response.data?.code !== 200
  //     ) {
  //       throw new Error(response.data?.message || "User not found.");
  //     }

  //     // Step 2: If user exists, send OTP
  //     await sendOTP(email);
  //     return {
  //       status: "Successful",
  //       code: 200,
  //       message: "OTP sent successfully!",
  //       pkCredentialMasterId: response.data.pkCredentialMasterId,
  //     };
  //   } catch (err) {
  //     return {
  //       status: "Failed",
  //       code: 400,
  //       message: err.message || "Failed to send recovery email",
  //     };
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Update Password Function — disabled along with the forgot-password flow above.
  // const updatePassword = async (pkCredentialMasterId, password) => {
  //   try {
  //     setLoading(true);
  //     const encryptedPassword = encodeData(password);
  //     const response = await axios.put(
  //       `${process.env.REACT_APP_API_BASE_URL}/register/update/${pkCredentialMasterId}`,
  //       { password: encryptedPassword }
  //     );
  //     setLoading(false);
  //     return response.data; // Return the response data
  //   } catch (err) {
  //     setLoading(false);
  //     throw err; // Ensure errors are properly caught
  //   }
  // };
  // const updatePassword = async (pkCredentialMasterId, password) => {
  //   try {
  //     setLoading(true);
  //     const encryptedPassword = encodeData(password);
  //     await axios.put(`${process.env.REACT_APP_API_BASE_URL}/register/update/${pkCredentialMasterId}`, { password: encryptedPassword });
  //     toast.success("Password updated successfully!");
  //   } catch (err) {
  //     toast.error(err.response?.data || "Failed to update password");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        emailId,
        role,
        pkCredentialMasterId,
        loading,
        error,
        successMessage,
        sessionExpired,
        triggerSessionExpired,
        confirmSessionExpired,
        // isOTPVerified, sendOTP, verifyOTP, forgotPassword, updatePassword:
        // forgot-password flow disabled — see definitions above for how to re-enable.
        registerUser,
        loginUser,
        logout,
        isTokenExpired,
        setError,
        setSuccessMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
